import { useEffect, useMemo, useState } from 'react';

/** Selection scoped to the rows on screen: it is cleared whenever `resetKey` changes (page, filters). */
export const useRowSelection = <T>(rows: T[], keyOf: (row: T) => string, resetKey: unknown) => {
  const [keys, setKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    setKeys(new Set());
  }, [resetKey]);

  const selectedRows = useMemo(() => rows.filter((row) => keys.has(keyOf(row))), [rows, keys, keyOf]);
  const allSelected = rows.length > 0 && selectedRows.length === rows.length;
  const someSelected = selectedRows.length > 0 && !allSelected;

  const toggle = (row: T) =>
    setKeys((prev) => {
      const next = new Set(prev);
      const key = keyOf(row);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleAll = () => setKeys(allSelected ? new Set() : new Set(rows.map(keyOf)));

  return {
    selectedRows,
    isSelected: (row: T) => keys.has(keyOf(row)),
    allSelected,
    someSelected,
    toggle,
    toggleAll,
    clear: () => setKeys(new Set()),
  };
};

export type RowSelection<T> = ReturnType<typeof useRowSelection<T>>;
